import type { RichTextContentProps } from './types';
import { forwardRef, useContext } from 'react';
import parse from 'html-react-parser';
import { cls } from '@/utils';
import style from './RichText.module.scss';
import { RichTextDefaultArr } from '@/constants';
import { BuilderStoreContext } from '@builder.io/react';
import { containsGeoDependentVariables } from './geoUtils';
export type Ref = HTMLDivElement;

/**
 * Safely parses state variables from text wrapped in double braces
 * Example: "{{state.stateUs}}" becomes the value of state.stateUs
 * Returns empty string if the state variable is undefined or null
 */
const parseStateVariables = (text: string, state: Record<string, unknown>): string => {
  // Early return if no state variables are present (performance optimization)
  if (!text.includes('{{')) {
    return text;
  }

  // Regex to match {{state.variableName}} patterns (with optional spaces)
  const stateVariableRegex = /\{\{\s*state\.([a-zA-Z0-9_.]+)\s*\}\}/g;
  return text.replace(stateVariableRegex, (match, variablePath) => {
    try {
      // Validate the variable path doesn't have consecutive dots or other malformed patterns
      if (variablePath.includes('..') || variablePath.startsWith('.') || variablePath.endsWith('.')) {
        return '';
      }

      // Split the variable path (e.g., "stateUs" or "location.city")
      const pathParts = variablePath.split('.');

      // Safely navigate through the state object
      let value: unknown = state;
      for (const part of pathParts) {
        if (value === null || value === undefined) {
          return '';
        }

        // Handle arrays by checking if part is a valid array index
        if (Array.isArray(value)) {
          const index = parseInt(part, 10);
          if (isNaN(index) || index < 0 || index >= value.length) {
            return ''; // Invalid array index
          }
          value = value[index];
        }
        // Handle objects
        else if (value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, part)) {
          value = (value as Record<string, unknown>)[part];
        } else {
          // Property doesn't exist, return empty string
          return '';
        }
      }

      // Handle different value types safely
      if (value === null || value === undefined) {
        return '';
      }

      // Only stringify primitive values and avoid functions/complex objects
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
      }

      // For objects/arrays/functions, return empty string for safety
      return '';
    } catch (error) {
      // Log parsing errors for debugging Builder content issues
      if (process.env.NEXT_PUBLIC_ENVIRONMENT !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('RichText state variable parsing failed:', {
          variable: match,
          path: variablePath,
          error: error instanceof Error ? error.message : String(error)
        });
      }

      // If any error occurs during parsing, return empty string for safety
      return '';
    }
  });
};
const RichText = forwardRef<Ref, RichTextContentProps>(({
  className,
  text,
  ...props
}, ref) => {
  const builderState = useContext(BuilderStoreContext);

  // Handle case where builderState is null/undefined
  const state = builderState?.state || {};
  const _className = cls(style.root, className);
  if (typeof text === 'undefined' || typeof text !== 'string') return null;
  if (RichTextDefaultArr.includes(text.trim())) return null;

  // Check if text contains geo-dependent variables
  const hasGeoDependentVars = containsGeoDependentVariables(text);

  // Parse state variables before parsing HTML
  const processedText = parseStateVariables(text, state);
  return <div className={_className} {...props} ref={ref} suppressHydrationWarning={hasGeoDependentVars}>
			{parse(processedText)}
		</div>;
});
export default RichText;